import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const MoveCommitmentDialog = ({ commitment, open, onClose, onMoveSuccess }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [note, setNote] = useState('');
  const [isMoving, setIsMoving] = useState(false);

  const handleMove = async () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    const trimmedNote = note.trim();
    if (trimmedNote.length < 20) {
      toast.error('Reason must be at least 20 characters');
      return;
    }

    setIsMoving(true);
    try {
      // Format date in local timezone (avoid UTC conversion issues)
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      const token = localStorage.getItem('token');

      await axios.post(
        `/api/commitments/${commitment.id}/move`,
        { to_date: formattedDate, note: trimmedNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Commitment moved to ${formattedDate}`);
      onMoveSuccess();
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to move commitment';
      toast.error(message);
    } finally {
      setIsMoving(false);
    }
  };

  // Disable dates before today (not before commitment's original date)
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
  const disabledDates = { before: today };

  // Calculate move count
  const timesMoved = commitment.movements?.length || 0;
  const remainingMoves = 3 - timesMoved;
  const isAtLimit = timesMoved >= 3;
  const isNearLimit = timesMoved === 2;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move Commitment to New Date</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-sm text-gray-600 mb-2">
              <strong>Original Date:</strong> {commitment.entry_date}
            </div>
            {commitment.current_date && commitment.current_date !== commitment.entry_date && (
              <div className="text-sm text-gray-600 mb-2">
                <strong>Current Target Date:</strong> {commitment.current_date}
              </div>
            )}
            <div className="text-sm text-gray-600 mb-2">
              <strong>Gym:</strong> {commitment.gym_name}
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-gray-600">
                <strong>Moves:</strong>
              </span>
              <Badge variant={isNearLimit ? "destructive" : timesMoved > 0 ? "secondary" : "default"}>
                {timesMoved}/3 used
              </Badge>
              <span className={`text-xs ${remainingMoves === 1 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                {remainingMoves} {remainingMoves === 1 ? 'move' : 'moves'} remaining
              </span>
            </div>
            {isNearLimit && (
              <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-md mb-3">
                <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-orange-800">
                  <strong>Warning:</strong> This is the last move allowed for this commitment. After this move, it cannot be moved again.
                </div>
              </div>
            )}
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={disabledDates}
              className="rounded-md border"
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium">
                Reason <span className="text-red-500">*</span>
              </label>
              <span className={`text-xs ${note.trim().length >= 20 ? 'text-green-600' : 'text-gray-500'}`}>
                {note.trim().length}/20 min
              </span>
            </div>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Explain why this commitment is being moved (minimum 20 characters)"
              rows={3}
              className={`w-full ${note.trim().length > 0 && note.trim().length < 20 ? 'border-red-300' : ''}`}
            />
            {note.trim().length > 0 && note.trim().length < 20 && (
              <p className="text-xs text-red-500 mt-1">
                Please provide at least 20 characters
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isMoving}>
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={!selectedDate || note.trim().length < 20 || isMoving}>
            {isMoving ? 'Moving...' : 'Move Commitment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MoveCommitmentDialog;
